import { Component, Inject, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { CreateServiceStatusPayload, ServiceStatus, ServiceStatusAdminResponse, UpdateServiceStatusPayload } from 'src/app/global/services/service-state/service-state.model';
import {
    CreateServiceStatusAction,
    CreateServiceStatusErrorAction,
    CreateServiceStatusSuccessAction,
    DeleteServiceStatusAction,
    DeleteServiceStatusErrorAction,
    DeleteServiceStatusSuccessAction,
    GetServiceCategoriesErrorAction,
    GetServiceStatusesAction,
    GetServiceStatusesSuccessAction,
    UpdateServiceStatusAction,
    UpdateServiceStatusErrorAction,
    UpdateServiceStatusSuccessAction,
} from 'src/app/global/store/global.actions';
import { serviceStatusesSelector } from 'src/app/global/store/global.selectors';
import { State } from 'src/app/store';
import { BcitiCategoryInputComponent, CategoryInputState, CategoryOutput, PropertyLabelInput } from '../../forms/category/bciti-category-input.component';

@Component({
    selector: 'app-services-statuses-dialog',
    templateUrl: './services-statuses-dialog.component.html',
    styleUrls: ['./services-statuses-dialog.component.scss'],
})
export class ServicesStatusesDialogComponent implements OnInit {
    destroy$ = new Subject();

    creating = false;
    disableActions = false;
    toggleAddButton = true;

    colors = ['#6AB547', '#F03A47'];
    langs: string[] = [];

    loading = false;

    propertyLabelInput = PropertyLabelInput;

    @ViewChildren(BcitiCategoryInputComponent) statusInputComponents: QueryList<BcitiCategoryInputComponent>;

    statuses$ = this.store.select(serviceStatusesSelector).pipe(takeUntil(this.destroy$));

    constructor(
        @Inject(MAT_DIALOG_DATA) public data,
        private dialogRef: MatDialogRef<ServicesStatusesDialogComponent>,
        private store: Store<State>,
        private actions$: Actions,
    ) {}

    ngOnInit(): void {
        this.actions$
            .pipe(
                ofType(GetServiceStatusesAction),
                tap(() => (this.loading = true)),
            )
            .subscribe();

        this.actions$
            .pipe(
                ofType(GetServiceStatusesSuccessAction, GetServiceCategoriesErrorAction),
                tap(() => (this.loading = false)),
            )
            .subscribe();

        this.statuses$
            .pipe(
                take(1),
                filter((statuses) => statuses.length === 0),
                map(() => this.store.dispatch(GetServiceStatusesAction())),
            )
            .subscribe();
    }

    startCreating(): void {
        this.creating = true;
        this.toggleAddButton = false;
        this.statusInputComponents.forEach((input) => {
            if (input?.category?.name) {
                input.state = CategoryInputState.DISPLAYING;
            }
        });
    }

    handleCancelCreate(): void {
        this.creating = false;
        this.toggleAddButton = true;
    }

    toggleAddButtonDisplay($event) {
        return (this.toggleAddButton = $event === CategoryInputState.DISPLAYING);
    }

    closeAllOtherForms(targetStatus: ServiceStatus): void {
        this.creating = false;
        this.statusInputComponents.forEach((input) => {
            if (targetStatus?.name !== input?.category?.name) {
                input.state = CategoryInputState.DISPLAYING;
                input.serviceToDeleteFirst = [];
            }
        });
    }

    handleSave(e: CategoryOutput) {
        // all other inputs should be disabled / cannot be opened, buttons should be disabled except to close the dialog
        const statusInputRef = this.statusInputComponents.find((input) => input.id === e.inputId);
        if (e.category._id) {
            // patch
            this.updateStatus(e, statusInputRef);
        } else {
            // create
            this.createStatus(e, statusInputRef);
        }
    }

    updateStatus(e: CategoryOutput, inputRef: BcitiCategoryInputComponent): void {
        this.disableActions = true;
        inputRef.loading = true;

        this.store.dispatch(UpdateServiceStatusAction({ status: e.category as UpdateServiceStatusPayload }));

        this.actions$
            .pipe(
                ofType(UpdateServiceStatusSuccessAction, UpdateServiceStatusErrorAction),
                take(1),
                tap(() => {
                    inputRef.loading = false;
                    inputRef.state = CategoryInputState.DISPLAYING;
                    this.disableActions = false;
                    this.toggleAddButton = true;
                }),
            )
            .subscribe();
    }

    createStatus(e: CategoryOutput, inputRef: BcitiCategoryInputComponent): void {
        this.disableActions = true;
        inputRef.loading = true;

        this.store.dispatch(CreateServiceStatusAction({ status: e.category as CreateServiceStatusPayload }));

        this.actions$
            .pipe(
                ofType(CreateServiceStatusSuccessAction, CreateServiceStatusErrorAction),
                take(1),
                tap(() => {
                    inputRef.loading = false;
                    inputRef.state = CategoryInputState.DISPLAYING;
                    this.disableActions = false;
                    this.handleCancelCreate();
                    this.toggleAddButton = true;
                }),
            )
            .subscribe();
    }

    deleteStatus(e: CategoryOutput): void {
        const statusInputRef = this.statusInputComponents.find((input) => input.id === e.inputId);
        this.disableActions = true;
        statusInputRef.loading = true;

        this.store.dispatch(DeleteServiceStatusAction({ status: e.category as ServiceStatusAdminResponse }));

        this.actions$
            .pipe(
                ofType(DeleteServiceStatusSuccessAction),
                take(1),
                tap(() => {
                    statusInputRef.loading = false;
                    statusInputRef.state = CategoryInputState.DISPLAYING;
                    this.disableActions = false;
                    this.toggleAddButton = true;
                }),
            )
            .subscribe();

        this.actions$
            .pipe(
                ofType(DeleteServiceStatusErrorAction),
                take(1),
                tap((response) => {
                    if (response.error.status === 400) {
                        statusInputRef.serviceToDeleteFirst = response.error.error.data;
                    }
                    statusInputRef.loading = false;
                    this.disableActions = false;
                    this.toggleAddButton = true;
                }),
            )
            .subscribe();
    }

    close() {
        this.dialogRef.close();
    }
}
